import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../LanguageContext';
import axios from 'axios';
import LoadingSkeleton from '../components/LoadingSkeleton';
import EmptyState from '../components/EmptyState';
import AdDetailModal from '../components/AdDetailModal';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

const RED = '#EE1D52';
const tooltipStyle = {
  backgroundColor: 'var(--bg-card)', border: '1px solid var(--border)',
  borderRadius: 10, color: 'var(--text-primary)', boxShadow: 'var(--card-hover-shadow)',
};

const fmtK = n => { if (n == null) return '\u2014'; if (n >= 1e6) return (n/1e6).toFixed(1)+'M'; if (n >= 1e3) return (n/1e3).toFixed(1)+'K'; return Number(n).toLocaleString(); };
const fmtSAR = n => n != null ? Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' SAR' : '\u2014';

function getLocalDate(daysAgo) {
  const d = new Date(); d.setDate(d.getDate() - daysAgo);
  return d.toLocaleDateString('en-CA', { timeZone: 'Asia/Riyadh' });
}

export default function TikTokLive() {
  const { t, isRTL } = useLanguage();
  const [preset, setPreset] = useState('yesterday');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const [collapsedAgs, setCollapsedAgs] = useState(() => new Set()); // ad groups expanded by default; track the collapsed ones
  const [creativePreview, setCreativePreview] = useState(null);
  const [sortBy, setSortBy] = useState('spend');
  const [statusF, setStatusF] = useState('all');
  const [search, setSearch] = useState('');
  const [customFrom, setCustomFrom] = useState(getLocalDate(7));
  const [customTo, setCustomTo] = useState(getLocalDate(0));

  const getDates = useCallback(() => {
    switch (preset) {
      case 'today': return { from: getLocalDate(0), to: getLocalDate(0) };
      case 'yesterday': return { from: getLocalDate(1), to: getLocalDate(1) };
      case '3d': return { from: getLocalDate(3), to: getLocalDate(0) };
      case '7d': return { from: getLocalDate(7), to: getLocalDate(0) };
      case '30d': return { from: getLocalDate(30), to: getLocalDate(0) };
      case '90d': return { from: getLocalDate(90), to: getLocalDate(0) };
      case 'custom': return { from: customFrom, to: customTo };
      default: return { from: getLocalDate(30), to: getLocalDate(0) };
    }
  }, [preset, customFrom, customTo]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const { from, to } = getDates();
      const res = await axios.get('/api/tt/overview', { params: { date_from: from, date_to: to } });
      setData(res.data);
    } catch { setError(true); }
    setLoading(false);
  }, [getDates]);

  useEffect(() => { loadData(); }, [loadData]);

  const presets = [
    { key: 'today', label: t('execToday') || 'Today' },
    { key: 'yesterday', label: t('execYesterday') },
    { key: '3d', label: t('execLast3') },
    { key: '7d', label: t('execLast7') },
    { key: '30d', label: t('execLast30') },
    { key: '90d', label: t('execLastQuarter') },
    { key: 'custom', label: isRTL ? 'مخصص' : 'Custom' },
  ];

  // Real delivery state: prefer TikTok's secondary_status (whether it's ACTUALLY serving)
  // over `status`, which is only the on/off switch. Falls back to the switch for rows not
  // yet re-synced. Accepts a row object (or a bare status string for back-compat).
  const act = (row) => {
    const sec = (row && typeof row === 'object') ? (row.secondary_status || '') : '';
    if (sec) return /DELIVERY_OK|_ENABLE$|LIVE/i.test(sec);
    const o = (row && typeof row === 'object') ? (row.status || '') : (row || '');
    return o === 'ENABLE' || o === 'ACTIVE' || o === 'STATUS_ENABLE';
  };
  // Human label for the badge — shows WHY it isn't active (review / rejected / not started).
  const statusLabel = (row) => {
    if (act(row)) return t('active');
    const s = (((row && row.secondary_status) || '')).toUpperCase();
    if (/DENY|REJECT/.test(s)) return isRTL ? 'مرفوضة' : 'Rejected';
    if (/REVIEW|AUDIT/.test(s)) return isRTL ? 'قيد المراجعة' : 'In review';
    if (/NOT_START|NOT_DELIVER|NEW/.test(s)) return isRTL ? 'لم تبدأ' : 'Not started';
    return t('paused');
  };
  // Base = every campaign with activity in the range. The headline KPI cards sum THIS
  // (the account total), independent of the search/status filters that only narrow the
  // table below — otherwise typing in the search box would rewrite the top-line totals.
  const baseCampaigns = (data?.campaigns || []).filter(c => c.metrics?.spend > 0 || c.metrics?.impressions > 0);
  const campaigns = baseCampaigns
    .filter(c => statusF === 'all' || (statusF === 'active' ? act(c) : !act(c)))
    .filter(c => !search.trim() || (c.name || '').toLowerCase().includes(search.trim().toLowerCase()))
    .sort((a, b) => (b.metrics?.[sortBy] || 0) - (a.metrics?.[sortBy] || 0));

  // Totals — over the full activity base, not the filtered table.
  const tot = baseCampaigns.reduce((a, c) => {
    a.spend += c.metrics?.spend || 0; a.impressions += c.metrics?.impressions || 0;
    a.clicks += c.metrics?.clicks || 0; a.conversions += c.metrics?.conversions || 0;
    a.reach += c.metrics?.reach || 0;
    return a;
  }, { spend: 0, impressions: 0, clicks: 0, conversions: 0, reach: 0 });
  tot.ctr = tot.impressions > 0 ? (tot.clicks / tot.impressions) * 100 : 0;
  tot.cpc = tot.clicks > 0 ? tot.spend / tot.clicks : 0;
  tot.cpm = tot.impressions > 0 ? (tot.spend / tot.impressions) * 1000 : 0;
  // Reach can't be summed across campaigns (dedup), so use the exact account reach from the
  // platform report when available; fall back to the (approximate) sum otherwise.
  const accountReach = data?.reachExact ? (data.accountReach || 0) : tot.reach;
  const accountFreq = data?.reachExact ? (data.accountFrequency || 0) : 0;

  return (
    <div>
      <div className="filter-bar">
        <div className="tabs">
          {presets.map(p => (
            <button key={p.key} className={`tab ${preset === p.key ? 'active' : ''}`} onClick={() => setPreset(p.key)}>{p.label}</button>
          ))}
        </div>
        {preset === 'custom' && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="date" value={customFrom} max={customTo} onChange={e => setCustomFrom(e.target.value)} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.8rem' }} />
            <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{isRTL ? 'إلى' : 'to'}</span>
            <input type="date" value={customTo} min={customFrom} onChange={e => setCustomTo(e.target.value)} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.8rem' }} />
          </div>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          {data && <span className="text-secondary" style={{ fontSize: '0.78rem' }}>{campaigns.length} {t('execCampaigns')}</span>}
          <button className="btn btn-secondary btn-sm" onClick={loadData} disabled={loading}>
            {loading ? t('loading') : t('refresh')}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-4 mb-6">{[1,2,3,4].map(i => <div key={i} className="kpi-card"><LoadingSkeleton width="60%" height="14px" /><LoadingSkeleton width="80%" height="32px" style={{ marginTop: 8 }} /></div>)}</div>
      ) : error ? (
        <div className="card" style={{ textAlign: 'center', padding: 32 }}>
          <div style={{ fontSize: '1.6rem', marginBottom: 8 }}>&#9888;&#65039;</div>
          <div style={{ color: 'var(--text-secondary)', marginBottom: 12 }}>{isRTL ? 'تعذّر تحميل بيانات تيك توك' : 'Could not load TikTok data'}</div>
          <button className="btn btn-secondary btn-sm" onClick={loadData}>{t('refresh')}</button>
        </div>
      ) : baseCampaigns.length > 0 ? (
        <>
          <div className="grid grid-4 mb-4">
            <div className="kpi-card"><span className="kpi-title">{t('spend')}</span><span className="kpi-value" style={{ color: RED }}>{fmtSAR(tot.spend)}</span></div>
            <div className="kpi-card"><span className="kpi-title">{t('impressions')}</span><span className="kpi-value">{fmtK(tot.impressions)}</span></div>
            <div className="kpi-card"><span className="kpi-title">{t('clicks')}</span><span className="kpi-value">{fmtK(tot.clicks)}</span></div>
            <div className="kpi-card"><span className="kpi-title">{t('conversions')}</span><span className="kpi-value" style={{ color: 'var(--success)' }}>{fmtK(tot.conversions)}</span></div>
          </div>
          <div className="grid grid-4 mb-6">
            <div className="kpi-card"><span className="kpi-title">{t('ctr')}</span><span className="kpi-value" style={{ color: 'var(--success)' }}>{tot.ctr.toFixed(2)}%</span></div>
            <div className="kpi-card"><span className="kpi-title">{t('cpc')}</span><span className="kpi-value">{fmtSAR(tot.cpc)}</span></div>
            <div className="kpi-card"><span className="kpi-title">{t('cpm')}</span><span className="kpi-value">{fmtSAR(tot.cpm)}</span></div>
            <div className="kpi-card"><span className="kpi-title">{t('channelReach')}</span><span className="kpi-value">{fmtK(accountReach)}</span>{accountFreq > 0 && <span style={{ fontSize: '0.68rem', color: 'var(--text-secondary)' }}>{isRTL ? 'التكرار' : 'Freq'}: {accountFreq.toFixed(2)}</span>}</div>
          </div>

          <div className="card mb-6">
            <div className="card-header"><h2>{t('channelClicksByCampaign')}</h2></div>
            <div style={{ height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={campaigns.slice(0, 10)} layout="vertical" margin={{ left: 10, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis type="number" stroke="var(--text-secondary)" tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" width={160} stroke="var(--text-secondary)" tick={{ fill: 'var(--text-secondary)', fontSize: 10 }} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend />
                  <Bar dataKey="metrics.clicks" fill={RED} radius={[0, 6, 6, 0]} name={t('clicks')} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h2>{t('channelCampaignPerf')}</h2>
              <span className="text-secondary" style={{ fontSize: '0.75rem' }}>{isRTL ? 'اضغط: حملة ← مجموعة إعلانية ← إعلان' : 'Click: campaign → ad group → ad'}</span>
            </div>
            {/* Search + status filter + sort */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', padding: '0 4px 14px' }}>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder={isRTL ? 'بحث عن حملة…' : 'Search campaign…'}
                style={{ flex: '1 1 180px', minWidth: 140, padding: '7px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.82rem' }} />
              <div style={{ display: 'flex', gap: 4 }}>
                {[['all', isRTL ? 'الكل' : 'All'], ['active', isRTL ? 'نشطة' : 'Active'], ['paused', isRTL ? 'متوقفة' : 'Paused']].map(([v, lbl]) => (
                  <button key={v} onClick={() => setStatusF(v)} style={{ padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontSize: '0.76rem', fontWeight: statusF === v ? 700 : 500, border: `1px solid ${statusF === v ? RED : 'var(--border)'}`, background: statusF === v ? RED + '18' : 'transparent', color: statusF === v ? RED : 'var(--text-secondary)' }}>{lbl}</button>
                ))}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>{isRTL ? 'ترتيب:' : 'Sort:'}</span>
                <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.78rem' }}>
                  <option value="spend">{isRTL ? 'الإنفاق' : 'Spend'}</option>
                  <option value="impressions">{isRTL ? 'الظهور' : 'Impressions'}</option>
                  <option value="clicks">{isRTL ? 'النقرات' : 'Clicks'}</option>
                  <option value="conversions">{isRTL ? 'التحويلات' : 'Conversions'}</option>
                </select>
              </div>
            </div>
            <div className="data-table-wrapper">
              <table className="data-table" style={{ fontSize: '0.82rem' }}>
                <thead><tr>
                  <th></th>
                  <th>{t('campaign')}</th><th>{t('status')}</th>
                  <th>{t('impressions')}</th><th>{t('clicks')}</th><th>{t('spend')}</th>
                  <th>{t('ctr')}</th><th>{t('cpc')}</th><th>{t('cpm')}</th>
                  <th>{t('conversions')}</th>
                </tr></thead>
                <tbody>
                  {campaigns.map((c, i) => {
                    const m = c.metrics || {};
                    const isExpanded = expandedCampaign === c.campaign_id;
                    const adgroups = (c.adgroups || []).slice().sort((a, b) => (b.metrics?.spend || 0) - (a.metrics?.spend || 0));
                    const isActive = act(c);

                    return (
                      <React.Fragment key={i}>
                        {/* Campaign */}
                        <tr onClick={() => { setExpandedCampaign(isExpanded ? null : c.campaign_id); setCollapsedAgs(new Set()); }} style={{ cursor: 'pointer', borderRight: isExpanded ? '3px solid ' + RED : 'none' }}>
                          <td style={{ color: RED, fontSize: '0.7rem', width: 24 }}>{isExpanded ? '▲' : '▼'}</td>
                          <td style={{ fontWeight: 700 }}>{c.name}</td>
                          <td><span className={`badge badge-${isActive ? 'active' : 'paused'}`} title={c.secondary_status || c.status || ''}>{statusLabel(c)}</span></td>
                          <td>{fmtK(m.impressions)}</td>
                          <td style={{ fontWeight: 600 }}>{fmtK(m.clicks)}</td>
                          <td style={{ color: RED, fontWeight: 700 }}>{fmtSAR(m.spend)}</td>
                          <td style={{ color: 'var(--success)' }}>{m.ctr?.toFixed(2)}%</td>
                          <td>{fmtSAR(m.cpc)}</td>
                          <td>{fmtSAR(m.cpm)}</td>
                          <td>{m.conversions || 0}</td>
                        </tr>

                        {/* Ad groups — click to reveal ads */}
                        {isExpanded && adgroups.map((ag, gi) => {
                          const gm = ag.metrics || {};
                          const gExpanded = !collapsedAgs.has(ag.adgroup_id);
                          const gActive = act(ag);
                          const ads = (ag.ads || []).slice().sort((a, b) => (b.metrics?.spend || 0) - (a.metrics?.spend || 0));
                          return (
                            <React.Fragment key={'ag-' + gi}>
                              <tr onClick={() => setCollapsedAgs(prev => { const n = new Set(prev); n.has(ag.adgroup_id) ? n.delete(ag.adgroup_id) : n.add(ag.adgroup_id); return n; })} style={{ cursor: 'pointer', background: 'var(--accent-glow)' }}>
                                <td style={{ color: RED, fontSize: '0.62rem', textAlign: 'end' }}>{gExpanded ? '▲' : '▼'}</td>
                                <td style={{ fontSize: '0.8rem', paddingInlineStart: 18, fontWeight: 600 }}>
                                  <span style={{ marginInlineEnd: 5 }}>📁</span>{ag.name || 'Ad group'}
                                  <span style={{ fontSize: '0.65rem', color: 'var(--text-secondary)', marginInlineStart: 6 }}>({ads.length})</span>
                                </td>
                                <td><span className={`badge badge-${gActive ? 'active' : 'paused'}`} style={{ fontSize: '0.58rem' }} title={ag.secondary_status || ag.status || ''}>{statusLabel(ag)}</span></td>
                                <td style={{ fontSize: '0.8rem' }}>{fmtK(gm.impressions)}</td>
                                <td style={{ fontSize: '0.8rem' }}>{fmtK(gm.clicks)}</td>
                                <td style={{ fontSize: '0.8rem', color: RED, fontWeight: 700 }}>{fmtSAR(gm.spend)}</td>
                                <td style={{ fontSize: '0.8rem', color: 'var(--success)' }}>{Number(gm.ctr || 0).toFixed(2)}%</td>
                                <td style={{ fontSize: '0.8rem' }}>{fmtSAR(gm.cpc)}</td>
                                <td style={{ fontSize: '0.8rem' }}>{fmtSAR(gm.cpm)}</td>
                                <td style={{ fontSize: '0.8rem' }}>{gm.conversions || 0}</td>
                              </tr>

                              {gExpanded && ads.map((ad, ai) => {
                                const am = ad.metrics || {};
                                const adActive = act(ad);
                                return (
                                  <tr key={'ad-' + ai} style={{ background: 'var(--bg-secondary)' }}>
                                    <td></td>
                                    <td style={{ fontSize: '0.76rem', paddingInlineStart: 40 }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        {ad.thumbnail_url
                                          ? <img src={ad.thumbnail_url} alt="" loading="lazy" className="cmp-thumb"
                                              onClick={(e) => { e.stopPropagation(); setCreativePreview(ad); }}
                                              style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 8, cursor: 'pointer', flexShrink: 0, border: '1px solid var(--border)' }} />
                                          : <div onClick={(e) => { e.stopPropagation(); setCreativePreview(ad); }} style={{ width: 36, height: 36, borderRadius: 8, flexShrink: 0, cursor: 'pointer', background: 'linear-gradient(135deg,#ee1d52,#000)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.85rem' }}>{ad.media_type === 'video' ? '▶' : '🖼'}</div>}
                                        <span style={{ cursor: 'pointer' }} onClick={() => setCreativePreview(ad)}>{ad.name || ad.ad_name || 'Ad ' + (ai + 1)}</span>
                                        {data.adLevelLifetime && <span title={isRTL ? 'أرقام الإعلان إجمالية طوال العمر وليست للفترة المحددة' : 'Ad figures are lifetime totals, not the selected range'} style={{ fontSize: '0.54rem', fontWeight: 700, padding: '1px 5px', borderRadius: 5, background: 'var(--bg-primary)', color: 'var(--text-secondary)', border: '1px solid var(--border)' }}>{isRTL ? 'إجمالي' : 'lifetime'}</span>}
                                      </div>
                                    </td>
                                    <td><span className={`badge badge-${adActive ? 'active' : 'paused'}`} style={{ fontSize: '0.56rem' }} title={ad.secondary_status || ad.status || ''}>{statusLabel(ad)}</span></td>
                                    <td style={{ fontSize: '0.76rem' }}>{fmtK(Number(am.impressions || 0))}</td>
                                    <td style={{ fontSize: '0.76rem' }}>{fmtK(Number(am.clicks || 0))}</td>
                                    <td style={{ fontSize: '0.76rem', color: RED }}>{fmtSAR(Number(am.spend || 0))}</td>
                                    <td style={{ fontSize: '0.76rem', color: 'var(--success)' }}>{Number(am.ctr || 0).toFixed(2)}%</td>
                                    <td style={{ fontSize: '0.76rem' }}>{fmtSAR(Number(am.cpc || 0))}</td>
                                    <td style={{ fontSize: '0.76rem' }}>{fmtSAR(Number(am.cpm || 0))}</td>
                                    <td style={{ fontSize: '0.76rem' }}>{Number(am.conversions || am.conversion || 0)}</td>
                                  </tr>
                                );
                              })}
                              {gExpanded && ads.length === 0 && (
                                <tr style={{ background: 'var(--bg-secondary)' }}><td></td><td colSpan={9} style={{ fontSize: '0.74rem', color: 'var(--text-secondary)', padding: '10px 40px' }}>{t('execNoData')}</td></tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                        {isExpanded && adgroups.length === 0 && (
                          <tr style={{ background: 'var(--accent-glow)' }}>
                            <td></td>
                            <td colSpan={9} style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', textAlign: 'center', padding: 16 }}>No ad groups found</td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {campaigns.length === 0 && (
                    <tr><td colSpan={10} style={{ textAlign: 'center', padding: 18, color: 'var(--text-secondary)', fontSize: '0.82rem' }}>{isRTL ? 'لا توجد حملات مطابقة للتصفية' : 'No campaigns match the filter'}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <div className="card"><EmptyState icon="&#127925;" title={t('execNoData')} /></div>
      )}

      {creativePreview && (
        <AdDetailModal ad={creativePreview} platform="tiktok"
          onClose={() => setCreativePreview(null)} onChanged={() => loadData()} />
      )}
    </div>
  );
}
